import { useMutation, useQueryClient } from '@tanstack/react-query';
import { loaders } from './surveys.loaders';
import { surveysKeys } from './surveys.queries';

export const useCreateSurvey = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.createSurvey, {
    onSuccess: () => {
      return queryClient.invalidateQueries(surveysKeys.lists);
    },
  });
};

export const useGenerateTrainingFromPrompt = () => {
  return useMutation(loaders.generateTrainingFromPrompt);
};

export const useGenerateTrainingFromImport = () => {
  return useMutation(loaders.generateTrainingFromImport);
};

export const useUpdateSurvey = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.updateSurvey, {
    onSuccess: () => {
      return queryClient.invalidateQueries(surveysKeys.all);
    },
  });
};

export const useUpdateSurveyContent = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.updateSurveyContent, {
    onSuccess: () => {
      return queryClient.invalidateQueries(surveysKeys.all);
    },
  });
};

export const useUpdateSurveySettingsAndContent = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      surveyId,
      settings,
      content,
    }: {
      surveyId: string;
      settings: Parameters<typeof loaders.updateSurvey>[0]['data'];
      content: Parameters<typeof loaders.updateSurveyContent>[0]['data'];
    }) =>
      Promise.all([
        loaders.updateSurvey({ surveyId, data: settings }),
        loaders.updateSurveyContent({ surveyId, data: content }),
      ]),
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(surveysKeys.all);
      },
    },
  );
};

export const useActivateSurvey = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.activateSurvey, {
    onSuccess: () => {
      return queryClient.invalidateQueries(surveysKeys.all);
    },
  });
};

export const useDeactivateSurvey = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.deactivateSurvey, {
    onSuccess: () => {
      return queryClient.invalidateQueries(surveysKeys.all);
    },
  });
};

export const useCopySurvey = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.copySurvey, {
    onSuccess: () => {
      return queryClient.invalidateQueries(surveysKeys.all);
    },
  });
};

export const useSendSurveyNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.sendSurveyNotifications, {
    onSuccess: () => {
      return queryClient.invalidateQueries(surveysKeys.all);
    },
  });
};

export const useDeleteSurvey = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.deleteSurvey, {
    onSuccess: () => {
      return queryClient.invalidateQueries(surveysKeys.lists);
    },
  });
};

export const useUpdateSurveyGroupAssignments = () => {
  const queryClient = useQueryClient();

  return useMutation(loaders.updateSurveyGroupAssignments, {
    onSuccess: () => {
      return queryClient.invalidateQueries(
        surveysKeys.list('group-assignments'),
      );
    },
  });
};

export const useExportSurveyResults = () => {
  return useMutation(loaders.exportSurveyResults);
};

export const useResendNotifications = () => {
  return useMutation(loaders.resendNotifications);
};
